import { useCallback, useEffect, useRef, useState } from 'react';
import GameView from './components/GameView';
import LobbyView from './components/LobbyView';
import { tileSizeVars, opponentTileSizeVars } from './game/layout';
import { useBoardInput } from './hooks/useBoardInput';
import { useGameLogic } from './hooks/useGameLogic';
import { useInstallPrompt } from './hooks/useInstallPrompt';
import { useRoomConnection } from './hooks/useRoomConnection';

const ROOM_CODE_LENGTH = 5;
const SOLO_BOARD_POWERUPS = ['undo', 'switchTiles', 'deleteTile'];

function App() {
  const [phase, setPhase] = useState('lobby'); // lobby | game
  const [playerName, setPlayerName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [soloMode, setSoloMode] = useState(false);
  const [lastEvent, setLastEvent] = useState('');
  const [targetingOpponent, setTargetingOpponent] = useState(false);
  const [selectingDeleteTile, setSelectingDeleteTile] = useState(false);
  const [selectingSwitch, setSelectingSwitch] = useState(false);
  const [switchFirst, setSwitchFirst] = useState(null);

  const { installPromptEvent, isStandalone, handleInstallPwa } = useInstallPrompt(setLastEvent);
  const handleEnterGame = useCallback(() => setPhase('game'), []);

  const game = useGameLogic({ soloMode, setLastEvent });

  const handleJoinCodeChange = useCallback((code) => {
    setJoinCode(code.replace(/\D/g, '').slice(0, ROOM_CODE_LENGTH));
  }, []);

  const {
    roomCode,
    connectionStatus,
    opponent,
    roomStarted,
    canStart,
    isOnline,
    isHost,
    isWinner,
    isLoser,
    opponentFreezeSeconds,
    hostRoom,
    joinRoom,
    startGame,
    emitMove,
    emitPowerup,
    emitTargetedGarbage,
    restart,
    goOffline,
    clearWinner,
    addTargetedGarbageToOpponent,
  } = useRoomConnection({
    applyServerState: game.applyServerState,
    setLastEvent,
    setSoloMode,
    onEnterGame: handleEnterGame,
  });
  const opponentColorShiftMap =
    opponent?.colorShiftUntil && opponent.colorShiftUntil > Date.now() ? opponent.colorShiftMap : null;

  // Avoid flicker from brief socket reconnects by not tying this flag to isOnline.
  const waitingForStart = !soloMode && !!roomCode && !roomStarted;

  const handleMove = useCallback(
    (direction) => {
      if (selectingDeleteTile || selectingSwitch) {
        setLastEvent(selectingSwitch ? 'Zwei Tiles zum Tauschen wählen' : 'Feld zum Löschen anklicken');
        return;
      }
      if (isOnline && !soloMode) {
        if (!roomStarted) {
          setLastEvent('Warten auf Start');
          return;
        }
        emitMove(direction);
        return;
      }
      game.handleLocalMove(direction);
    },
    [emitMove, game, isOnline, roomStarted, selectingDeleteTile, selectingSwitch, setLastEvent, soloMode]
  );

  const triggerPowerup = useCallback(
    (type) => {
      const boardLocked = game.undoableMoves < 2;
      const isBoardPowerup = SOLO_BOARD_POWERUPS.includes(type);
      if (isBoardPowerup && boardLocked) {
        setLastEvent('Joker erst ab dem zweiten Zug nutzbar');
        return;
      }
      if (type === 'deleteTile') {
        if (game.powerups.deleteTile <= 0) return;
        if (isOnline && !soloMode && !roomStarted) {
          setLastEvent('Warten auf Start');
          return;
        }
        setSelectingDeleteTile(true);
        setLastEvent('Feld zum Löschen anklicken');
        return;
      }
      if (type === 'switchTiles') {
        if (game.powerups.switchTiles <= 0) return;
        if (isOnline && !soloMode && !roomStarted) {
          setLastEvent('Warten auf Start');
          return;
        }
        setSelectingSwitch(true);
        setSwitchFirst(null);
        setLastEvent('Erstes Tile zum Tauschen anklicken');
        return;
      }
      if (type === 'targetedGarbage') {
        if (soloMode) return;
        if (isOnline && !roomStarted) {
          setLastEvent('Warten auf Start');
          return;
        }
        if (!opponent) {
          setLastEvent('Kein Gegnerbrett verfügbar');
          return;
        }
        if (game.powerups.targetedGarbage <= 0) return;
        setTargetingOpponent(true);
        setLastEvent('Feld beim Gegner anklicken');
        return;
      }
      if (type === 'undo' && isOnline && !soloMode) {
        if (!roomStarted) {
          setLastEvent('Warten auf Start');
          return;
        }
        const local = game.triggerLocalPowerup('undo');
        emitPowerup(type);
        if (!local) {
          setLastEvent('Nichts zum Rückgängig machen');
        }
        return;
      }
      if (soloMode) {
        if (!SOLO_BOARD_POWERUPS.includes(type)) return;
        game.triggerLocalPowerup(type);
        return;
      }
      if (isOnline) {
        if (!roomStarted) {
          setLastEvent('Warten auf Start');
          return;
        }
        emitPowerup(type);
        return;
      }
      game.triggerLocalPowerup(type);
    },
    [emitPowerup, game, isOnline, opponent, roomStarted, selectingDeleteTile, selectingSwitch, setLastEvent, soloMode]
  );

  const resetGame = useCallback(() => {
    setTargetingOpponent(false);
    setSelectingDeleteTile(false);
    setSelectingSwitch(false);
    setSwitchFirst(null);
    if (isOnline && !soloMode) {
      if (!isHost) {
        setLastEvent('Nur der Host kann ein neues Spiel starten');
        return;
      }
      restart();
    } else {
      game.resetLocalGame();
      clearWinner();
    }
  }, [clearWinner, game, isHost, isOnline, restart, setLastEvent, soloMode]);

  const prevRoundEndedRef = useRef(false);
  useEffect(() => {
    if (!isOnline || soloMode) return;
    const roundEnded = !!(isWinner || isLoser);
    if (prevRoundEndedRef.current && !roundEnded) {
      setTargetingOpponent(false);
      setSelectingDeleteTile(false);
      setSelectingSwitch(false);
      setSwitchFirst(null);
    }
    prevRoundEndedRef.current = roundEnded;
  }, [isLoser, isOnline, isWinner, soloMode]);

  const startSolo = useCallback(() => {
    goOffline();
    setSoloMode(true);
    setPhase('game');
    setJoinCode('');
    setLastEvent('Solo-Runde gestartet');
    setSelectingDeleteTile(false);
    setSelectingSwitch(false);
    setSwitchFirst(null);
    game.resetLocalGame(true);
  }, [game, goOffline]);

  const handleHostRoom = useCallback(() => {
    const name = (playerName || '').trim();
    hostRoom(name);
  }, [hostRoom, playerName]);

  const handleJoinRoom = useCallback(() => {
    const name = (playerName || '').trim();
    joinRoom(joinCode, name);
  }, [joinCode, joinRoom, playerName]);

  const handleStartGame = useCallback(() => {
    if (!isOnline || soloMode) return;
    if (!isHost) return;
    if (!canStart) {
      setLastEvent('Warte auf zweiten Spieler');
      return;
    }
    startGame();
  }, [canStart, isHost, isOnline, setLastEvent, soloMode, startGame]);

  const handleOpponentCellClick = useCallback(
    (row, col, occupied) => {
      if (!targetingOpponent) return;
      if (!opponent) {
        setTargetingOpponent(false);
        setLastEvent('Kein Gegnerbrett');
        return;
      }
      if (occupied) {
        setLastEvent('Feld ist belegt');
        return;
      }
      const allowed = game.consumePowerup('targetedGarbage');
      if (!allowed) {
        setTargetingOpponent(false);
        return;
      }
      let applied = false;
      if (isOnline && !soloMode) {
        emitTargetedGarbage(row, col);
        applied = addTargetedGarbageToOpponent(row, col);
      } else {
        applied = addTargetedGarbageToOpponent(row, col);
      }
      setTargetingOpponent(false);
      setLastEvent(applied ? `Garbage platziert (${row + 1}, ${col + 1})` : 'Feld ist belegt');
    },
    [
      addTargetedGarbageToOpponent,
      emitTargetedGarbage,
      game,
      isOnline,
      opponent,
      setLastEvent,
      soloMode,
      targetingOpponent,
    ]
  );

  const handleLeaveLobby = useCallback(() => {
    if (isOnline && !soloMode) {
      goOffline();
    } else {
      game.resetLocalGame();
    }
    setTargetingOpponent(false);
    setSelectingDeleteTile(false);
    setSelectingSwitch(false);
    setSwitchFirst(null);
    setPhase('lobby');
    setSoloMode(false);
    setLastEvent('');
  }, [game, goOffline, isOnline, soloMode]);

  useEffect(() => {
    if (!isOnline || soloMode) return undefined;
    const handleUnload = () => {
      goOffline();
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [goOffline, isOnline, soloMode]);

  useEffect(() => {
    if (!opponent || soloMode) {
      setTargetingOpponent(false);
    }
  }, [opponent, soloMode]);

  const { handlePointerStart, handlePointerEnd, handlePointerCancel } = useBoardInput({
    phase,
    onMove: handleMove,
    onReset: resetGame,
  });

  const handleDeleteTileCellClick = useCallback(
    (row, col, occupied) => {
      if (!selectingDeleteTile) return;
      if (!occupied) {
        setLastEvent('Kein Tile zum Löschen');
        return;
      }
      if (isOnline && !soloMode) {
        const locallyRemoved = game.deleteTileImmediate(row, col);
        emitPowerup('deleteTile', { row, col });
        setSelectingDeleteTile(false);
        if (!locallyRemoved) {
          setLastEvent('Tile konnte nicht gelöscht werden');
        }
        return;
      }
      const ok = game.triggerLocalPowerup('deleteTile', { row, col });
      if (ok) setSelectingDeleteTile(false);
      else setLastEvent('Tile konnte nicht gelöscht werden');
    },
    [emitPowerup, game, isOnline, selectingDeleteTile, setLastEvent, soloMode]
  );

  const handleSwitchTilesClick = useCallback(
    (row, col, occupied) => {
      if (!selectingSwitch) return;
      if (!occupied) {
        setLastEvent('Bitte ein belegtes Feld wählen');
        setSelectingSwitch(false);
        setSwitchFirst(null);
        return;
      }
      if (!switchFirst) {
        setSwitchFirst({ row, col });
        setLastEvent('Zweites Tile zum Tauschen anklicken');
        return;
      }
      const second = { row, col };
      const tileA = game.tiles.find((t) => t.row === switchFirst.row && t.col === switchFirst.col);
      const tileB = game.tiles.find((t) => t.row === second.row && t.col === second.col);
      if (!tileA || !tileB) {
        setLastEvent('Tiles konnten nicht gefunden werden');
        setSelectingSwitch(false);
        setSwitchFirst(null);
        return;
      }
      if (tileA.value === tileB.value) {
        setLastEvent('Gleiche Werte werden nicht getauscht');
        setSelectingSwitch(false);
        setSwitchFirst(null);
        return;
      }
      if (isOnline && !soloMode) {
        const swapped = game.switchTilesImmediate(switchFirst, second);
        emitPowerup('switchTiles', {
          firstRow: switchFirst.row,
          firstCol: switchFirst.col,
          secondRow: second.row,
          secondCol: second.col,
        });
        setSelectingSwitch(false);
        setSwitchFirst(null);
        if (!swapped) setLastEvent('Tiles konnten nicht getauscht werden');
        return;
      }
      const ok = game.triggerLocalPowerup('switchTiles', { first: switchFirst, second });
      if (ok) {
        setSelectingSwitch(false);
        setSwitchFirst(null);
      } else {
        setLastEvent('Tiles konnten nicht getauscht werden');
      }
    },
    [emitPowerup, game, isOnline, selectingSwitch, setLastEvent, soloMode, switchFirst]
  );

  return (
    <div className="min-h-screen bg-white text-black flex items-center justify-center px-4 sm:px-6 pt-0.5 sm:pt-1 pb-2 sm:pb-3">
      {phase === 'lobby' ? (
        <LobbyView
          playerName={playerName}
          setPlayerName={setPlayerName}
          joinCode={joinCode}
          setJoinCode={handleJoinCodeChange}
          codeLength={ROOM_CODE_LENGTH}
          hasOpponent={!!opponent}
          roomCode={roomCode}
          connectionStatus={connectionStatus}
          hostRoom={handleHostRoom}
          joinRoom={handleJoinRoom}
          startSolo={startSolo}
          lastEvent={lastEvent}
          installPromptEvent={installPromptEvent}
          isStandalone={isStandalone}
          handleInstallPwa={handleInstallPwa}
        />
      ) : (
        <GameView
          score={game.score}
          best={game.best}
          roomCode={roomCode}
          connectionStatus={connectionStatus}
          codeLength={ROOM_CODE_LENGTH}
          opponent={opponent}
          opponentFreezeSeconds={opponentFreezeSeconds}
          tileSizeVars={tileSizeVars}
          opponentTileSizeVars={opponentTileSizeVars}
          waitingForStart={waitingForStart}
          targetingOpponent={targetingOpponent}
          onOpponentCellClick={handleOpponentCellClick}
          isHost={isHost}
          canStart={canStart}
          handleStartGame={handleStartGame}
          handleNewGame={resetGame}
          tiles={game.tiles}
          handlePointerStart={handlePointerStart}
          handlePointerEnd={handlePointerEnd}
          handlePointerCancel={handlePointerCancel}
          isFrozen={game.isFrozen}
          freezeSeconds={game.freezeSeconds}
          gameOver={game.gameOver}
          isWinner={isWinner || game.hasWon}
          isLoser={isLoser}
          powerups={game.powerups}
          colorShiftMap={game.colorShiftMap}
          opponentColorShiftMap={opponentColorShiftMap}
          undoableMoves={game.undoableMoves}
          selectingDeleteTile={selectingDeleteTile}
          selectingSwitch={selectingSwitch}
          switchFirst={switchFirst}
          onDeleteTileCellClick={handleDeleteTileCellClick}
          onSwitchTilesClick={handleSwitchTilesClick}
          soloMode={soloMode}
          triggerPowerup={triggerPowerup}
          lastEvent={lastEvent}
          onLeaveLobby={handleLeaveLobby}
        />
      )}
    </div>
  );
}

export default App;
